import { ISepApplicants, ISepApplicantsEvents } from '../types/Applications.interface';
import { ApplicationType, SepDateType } from './ApplicationsEnum';
import {
    addToDate,
    dayjsUtil,
    formatDate,
    getYearInNumber,
    isDateAfter,
    isDateSameOrAfter,
    isDateValid,
    parseDateWithFormat,
} from '../../../utils/dates/date.utils';
import { MM_DOT_DD_DOT_YYYY, MM_SLASH_DD_SLASH_YYYY } from '../../../utils/dates/dateFormats.utils';

/**
 * Converts a comma-separated string of coverage years into an array of numbers,
 * @param appCoverageYears - A string representing the coverage years, formatted as a comma-separated list enclosed in square brackets (e.g., "[2021,2022,2023]").
 * @param exchangeStartYear - A string representing the start year to filter coverage years, ensuring only years from this year onwards are included.
 * @returns An array of numbers representing the filtered coverage years.
 *          - Only years greater than or equal to the exchangeStartYear are included.
 */
export const getCoverageYearsBasedOnExchangeStartYear = (
    appCoverageYears: string,
    exchangeStartYear: string,
): number[] => {
    return appCoverageYears
        .slice(1, -1) // Remove the first and last characters
        .split(',') // Split by comma to get individual years
        .map(year => year.trim()) // Trim whitespace from each year
        .filter(year => year >= exchangeStartYear) // Filter years greater than or equal to exchangeStartYear
        .map(year => parseInt(year, 10)); // Convert remaining years to integers
};

/**
 * Formats the given APTC amount as a currency string.
 * @param aptc - The APTC amount to format, provided as a string. This can be a number with or without a decimal point.
 * @returns The formatted APTC amount as a string with a dollar sign, preserving or adding two decimal places as needed.
 *          - If the input is not a valid number, the original string is returned.
 *          - If the input contains a decimal point, it is returned with the original precision.
 *          - If the input is a whole number, it is formatted with two decimal places.
 */
export const formatAptc = (aptc: string): string => {
    const aptcNumber = parseFloat(aptc);
    if (isNaN(aptcNumber)) {
        return aptc;
    }
    return aptc.includes('.') ? `$${aptc}` : `$${aptcNumber.toFixed(2)}`;
};

/**
 * Calculates an override SEP (Special Enrollment Period) end date based on a grace period.
 *
 * This function adds a predefined number of days (60 days) plus an optional grace period
 * to the current date to determine the SEP end date. It returns both the Date object and
 * a formatted string representation of the end date.
 *
 * @param gracePeriod - A string representing the number of additional days to add to the 60-day period.
 *                      Defaults to '0' if not provided. This value is parsed as an integer.
 *
 * @returns An object containing:
 *  - `date`: A JavaScript Date object representing the calculated SEP end date.
 *  - `formattedDate`: A string representing the calculated SEP end date in 'MM/DD/YYYY' format.
 */
export const calculateOverrideSEPEndWithFormat = (gracePeriod: string = '0'): { date: Date; formattedDate: string } => {
    const timeSpan = 60 + parseInt(gracePeriod, 10);
    const endDate = addToDate(timeSpan, 'days');

    return {
        date: endDate.toDate(),
        formattedDate: endDate.format('MM/DD/YYYY'),
    };
};

export const validateStartBeforeEnd = (startDate, endDate, setError, sepDetails, applicantUpdatedSepEvents) => {
    startDate = formatDate(startDate, MM_SLASH_DD_SLASH_YYYY);
    endDate = formatDate(endDate, MM_SLASH_DD_SLASH_YYYY);
    if (startDate && endDate) {
        const isFutureDate = isDateSameOrAfter(endDate, dayjsUtil(), 'day');
        const formattedStartDate = formatDate(startDate, MM_SLASH_DD_SLASH_YYYY);
        const formattedEndDate = formatDate(endDate, MM_SLASH_DD_SLASH_YYYY);
        const isStartDateValid = isDateValid(formattedStartDate, MM_SLASH_DD_SLASH_YYYY);
        const isEndDateValid = isDateValid(formattedEndDate, MM_SLASH_DD_SLASH_YYYY);
        setError(error => ({
            ...error,
            startBeforeEnd: !isDateAfter(endDate, startDate, 'day'),
            future: !isFutureDate,
            invalidStartDate: !isStartDateValid,
            invalidEndDate: !isEndDateValid,
        }));
    } else {
        setError(error => ({
            ...error,
            startBeforeEnd: false,
            future: false,
            invalidStartDate: false,
            invalidEndDate: false,
        }));
    }

    if (applicantUpdatedSepEvents) {
        if (
            applicantUpdatedSepEvents.length === 0 &&
            startDate === sepDetails.previousSepStartDate &&
            endDate === sepDetails.previousEndDate
        ) {
            setError(error => ({ ...error, isUpdated: true }));
        } else {
            setError(error => ({ ...error, isUpdated: false }));
        }
    }
};

export const eventDateValidation = function ({
    formData,
    applicantInd,
    eventInd,
    eventId,
    eventDate,
    error,
    setError,
    serverDate,
}) {
    setError(error => ({
        ...error,
        [`${applicantInd}${eventInd}`]: { withinFuture60Days: false, withinPast60Days: false, within60Days: false },
    }));
    const eventType = formData.applicants[applicantInd].events[eventInd].eventList.find(
        event => event.eventId === eventId,
    )?.type;
    const allowFutureEvent = formData.applicants[applicantInd].events[eventInd].eventList.find(
        event => event.eventId === eventId,
    )?.allowFuture;
    if (eventType) {
        if (allowFutureEvent === 'Y') {
            //within past or future 60 days
            setError(error => ({
                ...error,
                [`${applicantInd}${eventInd}`]: {
                    ...error[`${applicantInd}${eventInd}`],
                    within60Days: !uiDateValidation(eventDate, 'within60Days', serverDate),
                },
            }));
        } else {
            //within past 60 days
            setError(error => ({
                ...error,
                [`${applicantInd}${eventInd}`]: {
                    ...error[`${applicantInd}${eventInd}`],
                    withinPast60Days: !uiDateValidation(eventDate, 'withinPast60Days', serverDate),
                },
            }));
        }
    }
};

function uiDateValidation(eventDate, type, serverDate) {
    eventDate = formatDate(eventDate, MM_SLASH_DD_SLASH_YYYY);
    serverDate = formatDate(serverDate, MM_SLASH_DD_SLASH_YYYY);
    if (eventDate !== undefined) {
        let date;
        // let serverDate = $('#serverDate').val();
        let dateToCompare = parseDateWithFormat(serverDate, MM_DOT_DD_DOT_YYYY);
        if (eventDate.length === 10) {
            date = eventDate;
        } else {
            date = eventDate.substring(0, 2) + '/' + eventDate.substring(2, 4) + '/' + eventDate.substring(4, 8);
        }

        let selectedDate = parseDateWithFormat(date, MM_DOT_DD_DOT_YYYY);
        let result = selectedDate.diff(dateToCompare, 'days');

        if (type === 'withinFuture60Days') {
            return dateToCompare.isSame(selectedDate, 'day') || (result <= 60 && result > 0);
        } else if (type === 'withinPast60Days') {
            return dateToCompare.isSame(selectedDate, 'day') || (result >= -60 && result < 0);
        } else if (type === 'within60Days') {
            return (
                dateToCompare.isSame(selectedDate, 'day') ||
                (result <= 60 && result > 0) ||
                (result >= -60 && result < 0)
            );
        }
    } else {
        return true;
    }
}

export function getApplicantEvents(sepData) {
    let applicantEvents: ISepApplicantsEvents[] = [];

    for (let i = 0; i < sepData.applicants.length; i++) {
        for (let j = 0; j < sepData.applicants[i].events.length; j++) {
            let eventObj = sepData.applicants[i].events[j];

            if (
                eventObj.eventId.toString() !== eventObj.previousEventId.toString() ||
                eventObj.eventDate !== eventObj.previousEventDate
            ) {
                let eventNameLabel = '';

                eventObj.eventList.forEach(function (item) {
                    if (item.eventId && eventObj.eventId) {
                        if (item.eventId.toString() === eventObj.eventId.toString()) {
                            eventNameLabel = item.eventNameLabel;
                        }
                    }
                });

                applicantEvents.push({
                    firstName: sepData.applicants[i].firstName,
                    middleName: sepData.applicants[i].middleName,
                    lastName: sepData.applicants[i].lastName,
                    eventId: eventObj.eventId,
                    eventDate: eventObj.eventDate,
                    applicantEventId: eventObj.applicantEventId,
                    eventNameLabel: eventNameLabel,
                    previousEventId: eventObj.previousEventId,
                    previousEventNameLabel: eventObj.previousEventNameLabel,
                });
            }
        }
    }

    //if QEP and there is change, that change must be for primary contact
    if (sepData.applicationType === ApplicationType.QEP && applicantEvents.length !== 0) {
        let eventId = applicantEvents[0].eventId;
        let eventDate = applicantEvents[0].eventDate;
        let eventNameLabel = applicantEvents[0].eventNameLabel;

        for (let i = 0; i < sepData.applicants.length; i++) {
            let applicantObj: ISepApplicants = sepData.applicants[i];
            if (applicantObj.personId !== 1) {
                applicantEvents.push({
                    firstName: applicantObj.firstName,
                    middleName: applicantObj.middleName,
                    lastName: applicantObj.lastName,
                    applicantEventId: applicantObj.events[0].applicantEventId,
                    previousEventId: applicantObj.events[0].eventId,
                    previousEventNameLabel: applicantObj.events[0].eventLabel,
                    eventId: eventId,
                    eventDate: eventDate,
                    eventNameLabel: eventNameLabel,
                });
            }
        }
    }

    return applicantEvents;
}

/**
 * Retrieves the SEP date based on the type and SEP details provided.
 *
 * @param {string} type - The type of date to retrieve ('MAX' for maximum date, otherwise minimum date).
 * @param {object} sepDetails - The details of the SEP application, including applicants and events.
 * @param {string} gracePeriod - The grace period to add to the maximum date if type is 'MAX'.
 * @returns {string} - The formatted SEP date.
 *
 * The function iterates through the applicants and their events to collect all event dates.
 * If the type is 'MAX', it calculates the maximum date and adds the grace period.
 * Otherwise, it calculates the minimum date.
 * The date is then formatted using the getUiDateFormat function.
 */
export function getSepDate(type, sepDetails, gracePeriod = '0') {
    const dates: Date[] = [];
    for (let i = 0; i < sepDetails.applicants.length; i++) {
        if (
            sepDetails.applicationType === ApplicationType.SEP ||
            (sepDetails.applicationType === ApplicationType.QEP && sepDetails.applicants[i].personId === 1)
        ) {
            for (let j = 0; j < sepDetails.applicants[i].events.length; j++) {
                dates.push(new Date(sepDetails.applicants[i].events[j].eventDate));
            }
        }
    }

    let dateToReturn;
    let oneDay = 24 * 60 * 60 * 1000;
    if (type === SepDateType.MAX) {
        let sepGracePeriod = parseInt(gracePeriod);
        dateToReturn = Math.max.apply(null, dates);

        dateToReturn += oneDay * (60 + sepGracePeriod);
    } else {
        dateToReturn = Math.min.apply(null, dates);
    }
    return getUiDateFormat(dateToReturn);
}

export const getUiDateFormat = dateToFormat => {
    const date = dateToFormat ? new Date(dateToFormat) : new Date();
    let dd: string | number = date.getDate();
    let mm: string | number = date.getMonth() + 1;
    let yyyy = date.getFullYear();

    if (mm < 10) {
        mm = '0' + mm;
    }

    if (dd < 10) {
        dd = '0' + dd;
    }
    return mm + '/' + dd + '/' + yyyy;
};

/**
 * Validates that the start date is before the end date in a shopping window.
 *
 * @param {Date} startDate - The start date of the shopping window.
 * @param {Date} endDate - The end date of the shopping window.
 * @returns {boolean} - Returns true if the start date is before the end date, otherwise false.
 *
 * The function formats the dates to MM/DD/YYYY format if they are not already in that format.
 * It then uses the utils functions to check if the end date is after the start date.
 */
export const validateStartBeforeEndShoppingWindow = (startDate: Date, endDate: Date): boolean => {
    let strStartDate = formatDate(startDate, MM_SLASH_DD_SLASH_YYYY);
    let strEndDate = formatDate(endDate, MM_SLASH_DD_SLASH_YYYY);

    if (strStartDate && strEndDate) {
        strStartDate =
            strStartDate.indexOf('/') > -1
                ? strStartDate
                : strStartDate.substring(0, 2) +
                  '/' +
                  strStartDate.substring(2, 4) +
                  '/' +
                  strStartDate.substring(4, 8);
        strEndDate =
            strEndDate.indexOf('/') > -1
                ? strEndDate
                : strEndDate.substring(0, 2) + '/' + strEndDate.substring(2, 4) + '/' + strEndDate.substring(4, 8);

        return !!isDateAfter(strEndDate, strStartDate, 'day');
    }
    return false;
};

/**
 * Determines if the coverage start date is within the specified current coverage year.
 *
 * @param {string} coverageStartDate - The start date in 'mmddyyyy' format.
 * @param {number} maCurrentCoverageYear - The year to validate against.
 *
 * @returns {boolean} - Returns `true` if the start date year matches the given year, otherwise `false`.
 */
export const checkCurrentYear = (coverageStartDate: string, maCurrentCoverageYear: number): boolean => {
    let covStDt: Date | undefined;

    if (coverageStartDate === '') {
        return false;
    }

    if (coverageStartDate) {
        const formattedDate = coverageStartDate.replace(/^(\d\d)(\d\d)(\d\d\d\d)$/, '$1/$2/$3');
        covStDt = new Date(Date.parse(formattedDate));
    }

    if (covStDt && covStDt.getFullYear() !== new Date(maCurrentCoverageYear, 1, 1).getFullYear()) {
        return false;
    }
    return true;
};

export const validateCoverageAfterPlanYear = (coverageStartDate: Date, maCurrentCoverageYear: string) => {
    let isValid = true;
    if (coverageStartDate.getFullYear() !== parseInt(maCurrentCoverageYear)) {
        isValid = false;
    }
    return isValid;
};

export const aptcAmntValidate = (applicants, maximumAPTC, setError) => {
    let isNan = isNaN(Number(maximumAPTC));
    let aptcZeroAmntErrorFlag = false;
    let aptcMaxAmntErrorFlag = false;
    maximumAPTC = parseInt(maximumAPTC);
    if (!isNan) {
        aptcZeroAmntErrorFlag = true;
        aptcMaxAmntErrorFlag = false;
        applicants.forEach((applicant, i) => {
            if (applicant.aptcEligibility == 'Yes') {
                aptcZeroAmntErrorFlag = false;
            }
        });
        if (aptcZeroAmntErrorFlag == true && maximumAPTC > 0) {
            aptcZeroAmntErrorFlag = true;
        } else {
            aptcZeroAmntErrorFlag = false;
            if (maximumAPTC < 0) {
                aptcMaxAmntErrorFlag = true;
            }
        }
    }

    setError(err => ({ ...err, aptcZeroAmntErrorFlag, aptcMaxAmntErrorFlag, isNan }));
};

export const eligibilityStartDateValidation = (newDate, maCurrentCoverageYear, setError) => {
    let differentYear = false;
    let invalidStartDate = false;
    if (newDate) {
        differentYear = getYearInNumber(newDate) !== maCurrentCoverageYear;
        const formattedStartDate = formatDate(newDate, MM_SLASH_DD_SLASH_YYYY);
        invalidStartDate = !isDateValid(formattedStartDate, MM_SLASH_DD_SLASH_YYYY);
    }
    setError(err => ({ ...err, differentYear, invalidStartDate }));
};

/**
 * Converts a date string from MM/DD/YY format to YYYY-MM-DD format.
 *
 * @param {string} dateString
 * @returns {string}
 */
export const convertDateFormat = (dateString: string) => {
    // Split the input date string into components
    const [month, day, year] = dateString.split('/');
    const fullYear = year.length === 2 ? `20${year}` : year;
    const formattedDate = `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

    return formattedDate;
};
